import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { CFormCheck } from "@coreui/react";
import apiService from "../../../service/apiService";
import { BOOKING_STATUS, PAYMENT_METHODS } from "../../../config/constants";
import { useCookie } from "../../../hooks/useCookie";
import { useDebounce } from "../../../hooks/useDebounce";
import RenderTable from "../../../components/ui/RenderTable";
import Pagination from "../../../components/ui/Pagination";
import { general } from "../../../locales/general";
import { toQuery, Chip, pluralize } from "../../../helpers/general";
import { cardStyle } from "../../Places/PlacesTable"; // adjust path
import EventReservationTable from "./EventReservationTable";
import { FilterField, PillSelect } from "../../Bookings/BookingFilters";
import { controlStyle } from "../../../helpers/constants";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const PAGE_SIZE = 10;

export default function EventReservation() {
  const [bookingStatus, setBookingStatus] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [onlyGuest, setOnlyGuest] = useState("false");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const params = useParams();
  const { token } = useCookie("vToken");
  const search = useDebounce(searchQuery, 2000);
  const [searchParams] = useSearchParams();

  const sort = searchParams.get("sort") ?? "createdAt:desc";

  useEffect(() => {
    setCurrentPage(1);
  }, [bookingStatus, paymentMethod, onlyGuest, searchQuery]);

  const { data: bookings, isLoading } = useQuery(
    [
      "getBookedEvents",
      params?.eventId,
      bookingStatus,
      paymentMethod,
      onlyGuest,
      token,
      currentPage,
      search,
      sort,
    ],
    () =>
      apiService.MakeGetRequest(
        `events/${params?.eventId}/bookings${toQuery({
          page: currentPage,
          limit: PAGE_SIZE,
          status: bookingStatus,
          "payment-method": paymentMethod,
          "only-guest": onlyGuest,
          q: search,
          sort,
        })}`,
        token,
      ),
    { keepPreviousData: true },
  );

  const totalItems = bookings?.meta?.totalItems;
  // const hasFilters =
  //   bookingStatus || paymentMethod || onlyGuest === "true" || searchQuery;

  const reset = () => {
    setBookingStatus("");
    setPaymentMethod("");
    setOnlyGuest("false");
    setSearchQuery("");
  };

  return (
    <section>
      {/* Title + count */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fs-4 fw-bold mb-0">Réservations</h5>
          {totalItems !== undefined && (
            <Chip tone="warning">
              {totalItems} {pluralize("Réservation", totalItems)}
            </Chip>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ ...cardStyle, padding: "12px 16px" }} className="mb-3">
        <nav className="d-flex flex-wrap align-items-end gap-3">
          <FilterField label="Statut">
            <PillSelect
              value={bookingStatus}
              onChange={setBookingStatus}
              allLabel="Toutes"
              options={[
                { value: BOOKING_STATUS.WAITING, label: "En attente" },
                { value: BOOKING_STATUS.CONFIRMED, label: "Approuvé" },
                { value: BOOKING_STATUS.PASSED, label: "Scanné" },
                { value: BOOKING_STATUS.CANCELED, label: "Annulé" },
              ]}
            />
          </FilterField>

          <FilterField label="Méthode de paiement">
            <PillSelect
              value={paymentMethod}
              onChange={setPaymentMethod}
              allLabel="Toutes"
              options={[
                {
                  value: PAYMENT_METHODS.CARTE_BANCAIRE,
                  label: "Carte bancaire",
                },
                { value: PAYMENT_METHODS.WAFACASH, label: "WafaCash" },
                { value: PAYMENT_METHODS.SUR_PLACE, label: "Sur place" },
                { value: PAYMENT_METHODS.STRIPE, label: "Stripe" },
              ]}
            />
          </FilterField>

          <FilterField label="Recherche">
            <div
              className="d-flex align-items-center gap-2 px-3"
              style={controlStyle}
            >
              <MagnifyingGlassIcon color="gray" width={18} />
              <input
                type="text"
                className="border-0 w-100 bg-transparent"
                style={{ outline: "none", fontSize: 13 }}
                placeholder="Rechercher"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </FilterField>

          <div className="d-flex align-items-center" style={{ height: 38 }}>
            <CFormCheck
              id="showOnlyGuest"
              label="GuestList uniquement"
              checked={onlyGuest === "true"}
              onChange={(e) =>
                setOnlyGuest(e.target.checked ? "true" : "false")
              }
            />
          </div>

          <button
            type="button"
            onClick={reset}
            className="btn btn-outline-danger rounded-pill px-3 ms-auto"
            style={{ height: 38, fontSize: 13 }}
          >
            Réinitialiser
          </button>
        </nav>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isLoading}
          render={() =>
            bookings?.items?.length > 0 ? (
              <EventReservationTable bookings={bookings.items} />
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        siblingCount={1}
        totalCount={totalItems}
        pageSize={PAGE_SIZE}
      />
    </section>
  );
}
